import React from "react";
import { useNavigate } from "react-router-dom";

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <button onClick={() => navigate("/")} className="flex items-center">
            <h1 className="text-2xl font-bold" style={{ color: '#30853A' }}>Plexys</h1>
          </button>
          <button
            onClick={() => navigate("/")}
            className="px-4 py-2 text-gray-600 hover:text-gray-900"
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms of Service</h1>
        <p className="text-sm text-gray-500 mb-8">Last Updated: {new Date().toLocaleDateString()}</p>

        <div className="prose prose-lg max-w-none space-y-6">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance and Binding Agreement</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">1.1 Agreement to Terms</h3>
            <p className="text-gray-700 mb-3">
              These Terms of Service ("Terms," "Agreement") constitute a legally binding agreement between you
              ("User," "you," "your," or "Subscriber") and Plexys Limited and its affiliates ("Plexys," "we,"
              "our," or "us") governing your access to and use of the Plexys referral management and market
              analysis platform, including all associated software, applications, features, content, and services
              (collectively, the "Service").
            </p>
            <p className="text-gray-700 mb-3">
              BY CLICKING "I AGREE," CREATING AN ACCOUNT, ACCESSING, OR USING THE SERVICE IN ANY MANNER, YOU:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Acknowledge that you have read, understood, and agree to be bound by these Terms</li>
              <li>Represent and warrant that you have the legal capacity and authority to enter into this Agreement</li>
              <li>Agree to comply with all applicable laws, regulations, and professional standards</li>
              <li>Accept our Privacy Policy, which is incorporated herein by reference</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE THE SERVICE.</strong>
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">1.2 Modifications to Terms</h3>
            <p className="text-gray-700 mb-3">
              We reserve the right to modify, amend, or update these Terms at any time in our sole discretion.
              Material changes will be communicated through email notification, in-application notices, or other
              reasonable means. Your continued use of the Service after such changes constitutes acceptance of the
              modified Terms. If you do not agree to modified Terms, you must discontinue use of the Service and
              may terminate your account as provided in Section 9.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">1.3 Additional Terms</h3>
            <p className="text-gray-700">
              Certain features or services may be subject to additional terms, conditions, or policies, which will
              be presented to you at the time you access such features or services. Such additional terms are
              incorporated into and form part of this Agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Service Description and Scope</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.1 Overview</h3>
            <p className="text-gray-700 mb-3">
              Plexys provides a cloud-based software-as-a-service (SaaS) platform designed for healthcare specialists
              and providers to manage and analyze referral relationships, track referral quality, and identify market
              opportunities. The Service includes, but is not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Referral tracking and management tools</li>
              <li>Referral quality assessment and scoring mechanisms</li>
              <li>Geographic market analysis and visualization through interactive heatmaps</li>
              <li>Provider network intelligence and data (integrating National Provider Identifier (NPI) registry
                  data, CMS utilization data, and other publicly available sources)</li>
              <li>Analytics, reporting, and dashboard features</li>
              <li>Data export and integration capabilities</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.2 Service Modifications</h3>
            <p className="text-gray-700 mb-3">
              We reserve the right to modify, suspend, discontinue, or restrict access to all or any part of the
              Service at any time, with or without notice, for any reason including but not limited to maintenance,
              updates, security concerns, or business decisions. We shall not be liable for any modification,
              suspension, or discontinuance of the Service.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">2.3 Service Limitations</h3>
            <p className="text-gray-700">
              The Service is provided as a business intelligence and analytics tool. It is NOT:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>A medical device or clinical decision support system</li>
              <li>A substitute for professional medical judgment, clinical expertise, or patient care decisions</li>
              <li>Intended for use in diagnosis, treatment, cure, mitigation, or prevention of disease</li>
              <li>A comprehensive or authoritative source of provider or healthcare data</li>
              <li>Guaranteed to be error-free, complete, accurate, or up-to-date</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.1 Account Creation</h3>
            <p className="text-gray-700 mb-3">
              To access the Service, you must create an account by providing accurate, current, and complete
              registration information. You represent and warrant that:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>All information you provide is truthful, accurate, current, and complete</li>
              <li>You are a licensed healthcare provider or authorized representative of a healthcare entity</li>
              <li>You are at least 18 years of age and have the legal capacity to enter into this Agreement</li>
              <li>You have the authority to bind any entity on whose behalf you are acting</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.2 Account Security and Responsibilities</h3>
            <p className="text-gray-700 mb-3">
              You are solely responsible for maintaining the confidentiality and security of your account
              credentials and for all activities that occur under your account. You agree to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Use a strong, unique password and enable multi-factor authentication if available</li>
              <li>Not share your account credentials with any third party</li>
              <li>Immediately notify us of any unauthorized access to or use of your account</li>
              <li>Accept full responsibility for all activities conducted through your account</li>
              <li>Maintain and promptly update your account information to ensure accuracy</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>You acknowledge and agree that you are solely responsible for any unauthorized use of
              your account and that Plexys shall not be liable for any loss or damage arising from your failure
              to maintain account security.</strong>
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">3.3 Prohibited Account Activities</h3>
            <p className="text-gray-700 mb-3">
              You may not:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Create an account using false, misleading, or inaccurate information</li>
              <li>Create multiple accounts for the same individual or entity</li>
              <li>Use another user's account without authorization</li>
              <li>Sell, transfer, or sublicense your account to any third party</li>
              <li>Use automated means (bots, scripts, etc.) to create accounts or access the Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Ownership, Privacy, and HIPAA Compliance</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 Your Data Ownership</h3>
            <p className="text-gray-700 mb-3">
              You retain all ownership rights to the de-identified referral data you input into the Service ("Your Data").
              Subject to the limited license granted in Section 4.2, Plexys does not claim any ownership rights in Your Data.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.2 License Grant to Plexys</h3>
            <p className="text-gray-700 mb-3">
              You grant Plexys a non-exclusive, worldwide, royalty-free license to use, process, store, transmit, and
              display Your Data solely for the purposes of:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Providing the Service to you</li>
              <li>Generating analytics, reports, and insights for your use</li>
              <li>Maintaining, improving, and developing the Service</li>
              <li>Creating aggregated, de-identified, anonymized data for research and analytics</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.3 No PHI Storage - Critical User Obligations</h3>
            <p className="text-gray-700 mb-3">
              <strong>THE SERVICE IS EXPRESSLY DESIGNED TO AVOID PROTECTED HEALTH INFORMATION (PHI)</strong> as defined
              under HIPAA. The Service does not store, process, or transmit PHI. You expressly covenant, warrant, and agree:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>NOT TO</strong> enter, upload, transmit, or store any PHI including but not limited to patient
                  names, Medical Record Numbers (MRNs), Social Security Numbers, dates of birth, diagnoses, treatment
                  information, clinical notes, or any other information that could identify individual patients</li>
              <li><strong>NOT TO</strong> use the Service in any manner that violates HIPAA, the HITECH Act, or any other
                  healthcare privacy laws or regulations</li>
              <li>To implement and maintain appropriate safeguards in your own practice to protect patient information</li>
              <li>To train all personnel with access to the Service on proper use and HIPAA compliance</li>
              <li>You are solely responsible for ensuring HIPAA compliance in your use of the Service</li>
            </ul>
            <p className="text-gray-700 mt-3">
              <strong>VIOLATION OF THIS SECTION MAY RESULT IN IMMEDIATE ACCOUNT TERMINATION WITHOUT REFUND AND MAY
              EXPOSE YOU TO CIVIL AND CRIMINAL PENALTIES UNDER HIPAA AND OTHER LAWS.</strong>
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">4.4 No Business Associate Agreement</h3>
            <p className="text-gray-700">
              Because the Service avoids PHI by design, Plexys does not act as a "Business Associate" under HIPAA, and
              no Business Associate Agreement is entered into or required. Plexys has no obligations under HIPAA with
              respect to the Service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Acceptable Use Policy and Prohibited Conduct</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Permitted Uses</h3>
            <p className="text-gray-700 mb-3">
              You may use the Service only for lawful purposes and in accordance with these Terms. Specifically, you may
              use the Service to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Track and analyze de-identified referral relationships and patterns</li>
              <li>Assess referral quality for practice improvement purposes</li>
              <li>Conduct market analysis and identify business development opportunities</li>
              <li>Generate reports and analytics for internal business purposes</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.2 Prohibited Uses</h3>
            <p className="text-gray-700 mb-3">
              You expressly agree NOT to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li><strong>Illegal Activities:</strong> Use the Service for any illegal, unlawful, or fraudulent purpose</li>
              <li><strong>PHI Violations:</strong> Upload, transmit, or store any PHI or violate HIPAA or other healthcare
                  privacy regulations</li>
              <li><strong>Security Violations:</strong> Attempt to gain unauthorized access to the Service, other users'
                  accounts, or our systems through hacking, password mining, or any other means</li>
              <li><strong>Malicious Code:</strong> Upload, transmit, or introduce viruses, worms, Trojan horses, malware,
                  or any other harmful code</li>
              <li><strong>Service Disruption:</strong> Interfere with, disrupt, or impose unreasonable burdens on the
                  Service or our servers, networks, or infrastructure</li>
              <li><strong>Reverse Engineering:</strong> Reverse engineer, decompile, disassemble, or attempt to discover
                  the source code of the Service</li>
              <li><strong>Data Scraping:</strong> Use automated means (bots, scrapers, crawlers) to access, collect, or
                  extract data from the Service</li>
              <li><strong>Intellectual Property Infringement:</strong> Copy, reproduce, distribute, modify, create derivative
                  works from, publicly display, or exploit the Service or any proprietary content</li>
              <li><strong>Impersonation:</strong> Impersonate any person or entity or misrepresent your affiliation with
                  any person or entity</li>
              <li><strong>Spam or Unsolicited Communications:</strong> Use the Service to send spam, unsolicited
                  advertisements, or promotional materials</li>
              <li><strong>Competitive Activities:</strong> Use the Service to develop, operate, or promote a competing service</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">5.3 Monitoring and Enforcement</h3>
            <p className="text-gray-700">
              We reserve the right, but have no obligation, to monitor your use of the Service for compliance with these
              Terms. We may investigate any reported or apparent violation and take any action we deem appropriate,
              including terminating your account without notice or refund.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property Rights</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.1 Plexys Intellectual Property</h3>
            <p className="text-gray-700 mb-3">
              The Service, including but not limited to all software, applications, algorithms, user interfaces,
              designs, graphics, logos, trademarks, service marks, trade names, content, features, functionality,
              and documentation (collectively, "Plexys IP"), is owned by and proprietary to Plexys and its licensors
              and is protected by United States and international copyright, trademark, patent, trade secret, and
              other intellectual property laws.
            </p>
            <p className="text-gray-700 mb-3">
              All rights, title, and interest in and to the Plexys IP remain exclusively with Plexys and its licensors.
              No rights are granted to you except as expressly set forth in these Terms.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.2 Limited License to Use Service</h3>
            <p className="text-gray-700 mb-3">
              Subject to your compliance with these Terms, Plexys grants you a limited, non-exclusive, non-transferable,
              non-sublicensable, revocable license to access and use the Service solely for your internal business
              purposes. This license does not include any right to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Reproduce, modify, create derivative works from, distribute, or publicly display any part of the Service</li>
              <li>Reverse engineer, decompile, or disassemble the Service or attempt to discover its source code</li>
              <li>Rent, lease, lend, sell, sublicense, assign, or transfer the Service to any third party</li>
              <li>Use the Service to build a competitive product or service</li>
              <li>Remove, alter, or obscure any proprietary notices (copyright, trademark, patent, etc.)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">6.3 Feedback</h3>
            <p className="text-gray-700">
              If you provide Plexys with any suggestions, comments, improvements, ideas, or other feedback ("Feedback")
              regarding the Service, you hereby assign to Plexys all rights, title, and interest in such Feedback, and
              Plexys shall be free to use, disclose, reproduce, license, distribute, and exploit such Feedback without
              any obligation or compensation to you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Disclaimers and Warranties</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 "AS IS" and "AS AVAILABLE" Disclaimer</h3>
            <p className="text-gray-700 mb-3">
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER
              EXPRESS, IMPLIED, OR STATUTORY, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR
              A PARTICULAR PURPOSE, TITLE, NON-INFRINGEMENT, ACCURACY, COMPLETENESS, OR ARISING FROM COURSE OF DEALING
              OR USAGE OF TRADE.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.2 Service Availability and Performance</h3>
            <p className="text-gray-700 mb-3">
              PLEXYS DOES NOT WARRANT THAT:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The Service will be uninterrupted, timely, secure, or error-free</li>
              <li>The results obtained from use of the Service will be accurate, complete, or reliable</li>
              <li>The quality of the Service will meet your expectations</li>
              <li>Any errors, bugs, or defects in the Service will be corrected</li>
              <li>The Service or servers are free of viruses or other harmful components</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.3 Third-Party Data Disclaimer</h3>
            <p className="text-gray-700 mb-3">
              The Service integrates data from third-party sources (NPI registry, CMS data, Google Places, etc.).
              PLEXYS MAKES NO REPRESENTATIONS OR WARRANTIES REGARDING THE ACCURACY, COMPLETENESS, TIMELINESS, OR
              RELIABILITY OF ANY THIRD-PARTY DATA. You acknowledge that third-party data may contain errors,
              omissions, or outdated information.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.4 Not Medical Advice or Clinical Tool</h3>
            <p className="text-gray-700 mb-3">
              <strong>CRITICAL DISCLAIMER:</strong> THE SERVICE IS A BUSINESS ANALYTICS AND INTELLIGENCE TOOL ONLY.
              IT IS NOT A MEDICAL DEVICE, CLINICAL DECISION SUPPORT SYSTEM, OR DIAGNOSTIC TOOL. THE SERVICE IS NOT
              INTENDED TO PROVIDE MEDICAL ADVICE, CLINICAL GUIDANCE, DIAGNOSIS, TREATMENT, OR PATIENT CARE RECOMMENDATIONS.
            </p>
            <p className="text-gray-700 mb-3">
              YOU ACKNOWLEDGE THAT:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>The Service does not replace professional medical judgment, clinical expertise, or patient care standards</li>
              <li>All clinical and patient care decisions remain solely your professional responsibility</li>
              <li>You must independently verify any information before relying on it for any business or clinical purpose</li>
              <li>Referral quality scores and analytics are subjective tools for practice improvement only</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">7.5 Your Assumption of Risk</h3>
            <p className="text-gray-700">
              YOU ACKNOWLEDGE AND AGREE THAT YOUR USE OF THE SERVICE IS AT YOUR SOLE RISK. YOU ARE SOLELY RESPONSIBLE
              FOR ANY DAMAGE TO YOUR COMPUTER SYSTEM, LOSS OF DATA, OR OTHER HARM THAT RESULTS FROM YOUR USE OF THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Limitation of Liability and Indemnification</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.1 Exclusion of Consequential Damages</h3>
            <p className="text-gray-700 mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL PLEXYS, ITS AFFILIATES, OFFICERS,
              DIRECTORS, EMPLOYEES, AGENTS, SUPPLIERS, OR LICENSORS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
              CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT NOT LIMITED TO:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Loss of profits, revenue, business, or anticipated savings</li>
              <li>Loss of data, goodwill, or reputation</li>
              <li>Business interruption or downtime</li>
              <li>Cost of procuring substitute services</li>
              <li>Personal injury or property damage</li>
              <li>HIPAA violations or penalties</li>
              <li>Professional liability or malpractice claims</li>
            </ul>
            <p className="text-gray-700 mt-3">
              ARISING OUT OF OR IN CONNECTION WITH THESE TERMS OR YOUR USE OF OR INABILITY TO USE THE SERVICE, WHETHER
              BASED ON CONTRACT, TORT (INCLUDING NEGLIGENCE), STRICT LIABILITY, OR ANY OTHER LEGAL THEORY, EVEN IF
              PLEXYS HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.2 Liability Cap</h3>
            <p className="text-gray-700 mb-3">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, PLEXYS'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR
              RELATING TO THESE TERMS OR YOUR USE OF THE SERVICE SHALL NOT EXCEED THE GREATER OF: (A) THE TOTAL AMOUNT
              PAID BY YOU TO PLEXYS IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO LIABILITY, OR (B) ONE
              HUNDRED DOLLARS ($100.00 USD).
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.3 Your Indemnification Obligations</h3>
            <p className="text-gray-700 mb-3">
              You agree to indemnify, defend, and hold harmless Plexys, its affiliates, and their respective officers,
              directors, employees, agents, successors, and assigns from and against any and all claims, demands,
              liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees and court costs)
              arising out of or relating to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Your use or misuse of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law, regulation, or third-party rights (including HIPAA, intellectual property
                  rights, or privacy rights)</li>
              <li>Your unauthorized entry of PHI into the Service</li>
              <li>Your Content or data uploaded to the Service</li>
              <li>Any negligence, willful misconduct, or illegal acts by you or your personnel</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">8.4 Essential Purpose</h3>
            <p className="text-gray-700">
              You acknowledge that the limitations of liability and disclaimers in this Section 8 and Section 7 reflect
              a reasonable allocation of risk and are fundamental elements of the bargain between you and Plexys. The
              Service would not be provided without these limitations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Term and Termination</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.1 Term</h3>
            <p className="text-gray-700">
              These Terms commence on the date you first access or use the Service and continue until terminated by
              either party as provided herein.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.2 Termination by You</h3>
            <p className="text-gray-700">
              You may terminate your account at any time by contacting customer support or using the account deletion
              feature in the Service. Termination will be effective within 30 days of your request.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.3 Termination by Plexys</h3>
            <p className="text-gray-700 mb-3">
              We reserve the right to suspend, restrict, or terminate your access to the Service, with or without notice,
              for any reason including but not limited to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Violation of these Terms or any applicable law or regulation</li>
              <li>Unauthorized entry of PHI or other prohibited content</li>
              <li>Fraud, abuse, or misuse of the Service</li>
              <li>Non-payment of fees (if applicable)</li>
              <li>Threat to the security, integrity, or availability of the Service</li>
              <li>Legal or regulatory requirements</li>
              <li>Discontinuation of the Service</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.4 Effect of Termination</h3>
            <p className="text-gray-700 mb-3">
              Upon termination:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>Your right to access and use the Service immediately ceases</li>
              <li>We may delete your account and Your Data in accordance with our data retention policies</li>
              <li>You remain liable for all obligations incurred prior to termination</li>
              <li>No refunds will be provided for prepaid subscription fees (if applicable)</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">9.5 Survival</h3>
            <p className="text-gray-700">
              The following sections shall survive termination: Sections 4 (Data Ownership), 6 (Intellectual Property),
              7 (Disclaimers), 8 (Limitation of Liability and Indemnification), 9.4 (Effect of Termination), 10 (Dispute
              Resolution), and 11 (General Provisions).
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Dispute Resolution and Governing Law</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">10.1 Governing Law</h3>
            <p className="text-gray-700">
              These Terms and any dispute arising out of or relating to these Terms or the Service shall be governed by
              and construed in accordance with the laws of the State of Delaware, United States, without regard to its
              conflict of law provisions.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">10.2 Jurisdiction and Venue</h3>
            <p className="text-gray-700">
              Any legal action or proceeding arising out of or relating to these Terms or the Service shall be brought
              exclusively in the federal or state courts located in New Castle County, Delaware. You hereby irrevocably
              consent to the personal jurisdiction and venue of such courts and waive any objection to such jurisdiction
              or venue.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">10.3 Informal Dispute Resolution</h3>
            <p className="text-gray-700">
              Before initiating any legal proceeding, you agree to first contact us at legal@plexys.com and attempt to
              resolve the dispute informally for at least 30 days. Most disputes can be resolved quickly and amicably
              through informal negotiation.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">10.4 Waiver of Class Actions</h3>
            <p className="text-gray-700">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, YOU AND PLEXYS AGREE THAT EACH PARTY MAY BRING CLAIMS
              AGAINST THE OTHER ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED
              CLASS, CONSOLIDATED, OR REPRESENTATIVE PROCEEDING. NEITHER PARTY MAY PARTICIPATE IN A CLASS ACTION LAWSUIT,
              CLASS-WIDE ARBITRATION, OR ANY OTHER REPRESENTATIVE PROCEEDING AGAINST THE OTHER.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">10.5 Limitation Period</h3>
            <p className="text-gray-700">
              Any claim or cause of action arising out of or relating to these Terms or the Service must be filed within
              one (1) year after the claim or cause of action arose, or it shall be forever barred.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. General Provisions</h2>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.1 Entire Agreement</h3>
            <p className="text-gray-700">
              These Terms, together with our Privacy Policy and any additional terms referenced herein, constitute the
              entire agreement between you and Plexys regarding the Service and supersede all prior or contemporaneous
              agreements, communications, and proposals, whether oral or written, between you and Plexys.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.2 Severability</h3>
            <p className="text-gray-700">
              If any provision of these Terms is held to be invalid, illegal, or unenforceable by a court of competent
              jurisdiction, such provision shall be modified to the minimum extent necessary to make it enforceable, or
              if it cannot be made enforceable, it shall be severed from these Terms. The remaining provisions shall
              remain in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.3 Waiver</h3>
            <p className="text-gray-700">
              No waiver by Plexys of any term or condition of these Terms shall be deemed a further or continuing waiver
              of such term or condition or any other term or condition. Any failure by Plexys to assert a right or provision
              under these Terms shall not constitute a waiver of such right or provision.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.4 Assignment</h3>
            <p className="text-gray-700">
              You may not assign, transfer, or delegate these Terms or your rights and obligations hereunder without our
              prior written consent. Any attempted assignment in violation of this section shall be null and void. Plexys
              may freely assign these Terms without restriction. These Terms shall be binding upon and inure to the benefit
              of the parties and their respective successors and permitted assigns.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.5 Force Majeure</h3>
            <p className="text-gray-700">
              Plexys shall not be liable for any failure or delay in performance under these Terms due to causes beyond
              its reasonable control, including acts of God, natural disasters, war, terrorism, labor disputes, government
              actions, Internet or telecommunications failures, or third-party service provider failures.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.6 Notices</h3>
            <p className="text-gray-700">
              All notices to Plexys under these Terms shall be sent to legal@plexys.com. Notices to you may be sent to
              the email address associated with your account or through in-application notifications. Notices shall be
              deemed given when sent.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.7 Export Compliance</h3>
            <p className="text-gray-700">
              The Service may be subject to U.S. export control laws and regulations. You agree to comply with all
              applicable export laws and not to export, re-export, or transfer the Service to any prohibited country,
              entity, or person.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.8 U.S. Government Rights</h3>
            <p className="text-gray-700">
              If you are a U.S. federal, state, or local government entity, the Service is a "commercial item" as
              defined in 48 C.F.R. § 2.101, and is licensed with only those rights as are granted to all other users
              under these Terms.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-4">11.9 Contact Information</h3>
            <p className="text-gray-700">
              For questions about these Terms, please contact us at:<br />
              <strong>Plexys Limited</strong><br />
              Attention: Legal Department<br />
              Email: legal@plexys.com<br />
              Support: support@plexys.com
            </p>
          </section>

          <section className="bg-gray-100 p-6 rounded-lg mt-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Acknowledgment</h2>
            <p className="text-gray-700">
              BY USING THE SERVICE, YOU ACKNOWLEDGE THAT YOU HAVE READ THESE TERMS OF SERVICE, UNDERSTAND THEM, AND
              AGREE TO BE BOUND BY THEM. IF YOU DO NOT AGREE TO THESE TERMS, YOU MUST NOT ACCESS OR USE THE SERVICE.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
